import React, { useEffect, useRef, useState } from 'react';
import PageHero from '../components/PageHero';
import Seo from '../components/Seo';
import { Phone, Send, Check, AlertCircle } from 'lucide-react';
import { CONTACT, SERVICES, THERAPISTS } from '../mock';
import { validateName, validateEmail, validatePhone, isSuspiciousDuplicate } from '../lib/validation';

const noop = () => {};
const RATE_LIMIT_MS = 60_000; // 60s between submissions per browser

const encode = (data) =>
  Object.keys(data)
    .map((k) => encodeURIComponent(k) + '=' + encodeURIComponent(data[k]))
    .join('&');

export default function Schedule() {
  const [form, setForm] = useState({ name:'', email:'', phone:'', service:'', therapist:'', mode:'In-Person', insurance:'', insuranceName:'', notes:'' });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const mountedAtRef = useRef(Date.now());

  useEffect(() => { mountedAtRef.current = Date.now(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const [touched, setTouched] = useState({});
  const touch = (k) => () => setTouched((t) => ({ ...t, [k]: true }));

  // Per-field validation errors (null when valid).
  const errors = {
    name: validateName(form.name, 'Full name'),
    email: validateEmail(form.email),
    phone: validatePhone(form.phone),
  };
  const isFormValid = !errors.name && !errors.email && !errors.phone;

  const submit = async (e) => {
    e.preventDefault();
    setError('');

    // Anti-bot #1 — honeypot: if either hidden field has anything, silently drop.
    const hp1 = e.target.querySelector('input[name="bot-field"]')?.value || '';
    const hp2 = e.target.querySelector('input[name="website"]')?.value || '';
    if (hp1.trim() || hp2.trim()) {
      // Look like success to the bot but never send.
      setSent(true);
      return;
    }

    // Anti-bot #2 — minimum time on page (bots submit in milliseconds).
    if (Date.now() - mountedAtRef.current < 2500) {
      setError('Please take a moment to review your details before submitting.');
      return;
    }

    // Field-level validation
    const nameErr = validateName(form.name, 'Full name');
    const emailErr = validateEmail(form.email);
    const phoneErr = validatePhone(form.phone);
    if (nameErr || emailErr || phoneErr) {
      setError(nameErr || emailErr || phoneErr);
      setTouched({ name: true, email: true, phone: true });
      return;
    }

    // Cross-field: reject if all identical (bot signal)
    if (isSuspiciousDuplicate([form.name, form.email, form.phone])) {
      setError('Please fill in each field with different content.');
      return;
    }

    // Anti-abuse #3 — per-browser rate limit
    try {
      const last = parseInt(localStorage.getItem('ccc_last_submit') || '0', 10);
      if (last && Date.now() - last < RATE_LIMIT_MS) {
        setError('You just submitted a request — please wait a minute before sending another.');
        return;
      }
    } catch (_) { noop(); }

    setSubmitting(true);
    try {
      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode({
          'form-name': 'schedule',
          'bot-field': '',
          website: '',
          ...form,
        }),
      });
      try { localStorage.setItem('ccc_last_submit', String(Date.now())); } catch(_) { noop(); }
    } catch (_) { noop(); }
    try { const l = JSON.parse(localStorage.getItem('ccc_appts')||'[]'); l.push({ ...form, ts: Date.now() }); localStorage.setItem('ccc_appts', JSON.stringify(l)); } catch(_) { noop(); }
    setSubmitting(false);
    setSent(true);
  };

  return (
    <>
      <Seo
        title="Schedule an Appointment"
        description="Request an appointment with Chesapeake Counseling Center. In-person in Chesapeake, VA or telehealth throughout Virginia."
        path="/schedule"
      />
      <PageHero
        eyebrow="Schedule"
        title="Request an"
        highlight="Appointment"
        subtitle="Reach out today — taking the first step can feel hard, but you don’t have to figure it out alone."
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Schedule' }]}
      />

      <section className="section-pad" style={{ background: 'var(--warm-white)' }}>
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="card-soft p-8 md:p-10">
            {sent ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: 'rgba(232,165,169,0.2)' }}><Check className="w-7 h-7 text-[color:var(--dusty-rose)]" /></div>
                <div className="font-script text-3xl text-[color:var(--dusty-rose)] mb-2">Thank you</div>
                <p className="text-[color:var(--taupe-gray)] max-w-md mx-auto">Your request has been received. Our team will reach out to confirm your appointment within one business day.</p>
                <p className="mt-4 text-sm text-[color:var(--taupe-gray)]">Prefer to call? <a href={CONTACT.phoneHref} className="link-underline">{CONTACT.phone}</a></p>
              </div>
            ) : (
              <form
                onSubmit={submit}
                name="schedule"
                method="POST"
                data-netlify="true"
                data-netlify-honeypot="bot-field"
                className="space-y-5"
              >
                <input type="hidden" name="form-name" defaultValue="schedule" />
                {/* Honeypot #1 — bots that fill hidden fields get dropped by Netlify */}
                <p hidden><label>Don&apos;t fill this out: <input name="bot-field" defaultValue="" /></label></p>
                {/* Honeypot #2 — visually hidden but named to bait bots */}
                <div style={{ position: 'absolute', left: '-9999px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }} aria-hidden="true">
                  <label>Website<input type="text" name="website" tabIndex="-1" autoComplete="off" defaultValue="" /></label>
                </div>

                {error && (
                  <div className="rounded-xl px-4 py-3 text-sm flex items-start gap-2" style={{ background: 'rgba(232,165,169,0.15)', color: '#8B3A44' }}>
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> {error}
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Full Name" name="name" value={form.name} onChange={set('name')} onBlur={touch('name')} required minLength={2} error={touched.name && errors.name} />
                  <Field label="Phone" name="phone" type="tel" value={form.phone} onChange={set('phone')} onBlur={touch('phone')} required pattern="[0-9()+\-.\s]{7,}" error={touched.phone && errors.phone} />
                </div>
                <Field label="Email" name="email" type="email" value={form.email} onChange={set('email')} onBlur={touch('email')} required error={touched.email && errors.email} />

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--sage-dark)] font-semibold" htmlFor="schedule-service">Service Interest</label>
                    <select id="schedule-service" name="service" value={form.service} onChange={set('service')} className="mt-1.5 w-full rounded-xl border bg-white px-4 py-2.5 text-sm" style={{ borderColor: 'var(--soft-border)' }}>
                      <option value="">Select a service</option>
                      {SERVICES.map(s => <option key={s.slug} value={s.title}>{s.title}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--sage-dark)] font-semibold" htmlFor="schedule-therapist">Preferred Therapist</label>
                    <select id="schedule-therapist" name="therapist" value={form.therapist} onChange={set('therapist')} className="mt-1.5 w-full rounded-xl border bg-white px-4 py-2.5 text-sm" style={{ borderColor: 'var(--soft-border)' }}>
                      <option value="">No preference</option>
                      {THERAPISTS.map(t => <option key={t.slug} value={t.name}>{t.name}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--sage-dark)] font-semibold">Appointment Type</label>
                  <input type="hidden" name="mode" value={form.mode} />
                  <div className="mt-2 flex gap-3">
                    {['In-Person','Telehealth'].map(m => (
                      <button type="button" key={m} onClick={() => setForm({...form, mode:m})} className={`px-5 py-2.5 rounded-full text-sm border transition ${form.mode === m ? 'bg-[color:var(--dusty-rose)] text-white border-transparent' : 'bg-white text-[color:var(--deep-sage)]'}`} style={form.mode !== m ? { borderColor: 'var(--soft-border)' } : {}}>{m}</button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--sage-dark)] font-semibold" htmlFor="schedule-insurance">Insurance / Payment</label>
                  <select id="schedule-insurance" name="insurance" value={form.insurance} onChange={set('insurance')} className="mt-1.5 w-full rounded-xl border bg-white px-4 py-2.5 text-sm" style={{ borderColor: 'var(--soft-border)' }}>
                    <option value="">Select insurance or payment method…</option>
                    <option value="Aetna">Aetna</option>
                    <option value="Anthem / BCBS">Anthem / Blue Cross Blue Shield</option>
                    <option value="Cigna">Cigna</option>
                    <option value="Optum / United Healthcare">Optum / United Healthcare</option>
                    <option value="Tricare">Tricare</option>
                    <option value="Medicaid">Medicaid</option>
                    <option value="Medicare">Medicare</option>
                    <option value="Other Insurance">Other insurance</option>
                    <option value="Self-Pay / Cash">Self-pay / Cash</option>
                    <option value="Not Sure">Not sure yet</option>
                  </select>
                  <p className="mt-1 text-[11px] text-[color:var(--taupe-gray)]">Let us know what insurance you plan to use, or if you’ll be paying cash / self-pay.</p>
                </div>
                <Field label="Insurance / Plan Name" name="insuranceName" value={form.insuranceName} onChange={set('insuranceName')} placeholder="e.g., Anthem HealthKeepers, Tricare Prime, Aetna PPO…" />

                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--sage-dark)] font-semibold" htmlFor="schedule-notes">Anything else you’d like us to know?</label>
                  <textarea id="schedule-notes" name="notes" rows={4} value={form.notes} onChange={set('notes')} className="mt-1.5 w-full rounded-xl border bg-white px-4 py-3 text-sm" style={{ borderColor: 'var(--soft-border)' }} />
                </div>

                <button
                  type="submit"
                  disabled={submitting || !isFormValid}
                  className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                  title={!isFormValid ? 'Please complete Name, Phone, and Email to submit.' : ''}
                >
                  <Send className="w-4 h-4" /> {submitting ? 'Sending…' : 'Request Appointment'}
                </button>
                {!isFormValid && (
                  <div className="text-xs text-center text-[color:var(--taupe-gray)]">
                    Please fill in your name, a valid email, and phone number to submit.
                  </div>
                )}
                <div className="text-center text-sm text-[color:var(--taupe-gray)] flex items-center justify-center gap-1.5">
                  <Phone className="w-4 h-4" /> Prefer to call? <a href={CONTACT.phoneHref} className="link-underline">{CONTACT.phone}</a>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

function Field({ label, name, error, ...rest }) {
  const id = `schedule-${name || label.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <div>
      <label htmlFor={id} className="text-xs uppercase tracking-[0.2em] text-[color:var(--sage-dark)] font-semibold flex items-center justify-between">
        <span>{label}</span>
        {error && <span className="text-[10px] normal-case tracking-normal text-[color:var(--dusty-rose)] font-medium">{error}</span>}
      </label>
      <input
        id={id}
        name={name}
        {...rest}
        className="mt-1.5 w-full rounded-xl border-2 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--blush-pink)] transition"
        style={{ borderColor: error ? '#E8A5A9' : 'var(--soft-border)' }}
      />
    </div>
  );
}
